import { useAuthStore } from "@features/auth/store/authStore";
import SearchForm from "@shared/components/SearchForm";
import { PlusIcon } from "@shared/components/ui/Icon";
import { useProfile } from "@shared/hooks/useProfile";
import { User } from "@shared/types/user";
import theme from "@shared/utils/themeColors";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import toast from "react-hot-toast";
import Transition from "../../../utils/Transition";
import { useChitChat } from "../context/ChitChatContext";
import {
  ConversationsProvider,
  useConversations,
} from "../context/ConversationsContext";
import { useGroupConversations } from "../hooks/useGroupConversations";
import { GroupService } from "../services/group.service";
import { buildUserMap } from "../services/userMap.service";
import { useUserMapStore } from "../store/userMapStore";
import { Conversation } from "../types";
import CreateGroupModal from "./CreateGroupModal";
import DirectMessages from "./DirectMessages";

interface ChitChatDropdownProps {
  align?: "left" | "right";
}

const ChitChatDropdownContent: React.FC<ChitChatDropdownProps> = ({
  align = "right",
}) => {
  const { isDropdownOpen, closeDropdown, openChatWindow } = useChitChat();
  const dropdown = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [groupTitle, setGroupTitle] = useState("");
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [color, setColor] = useState("#6366f1");
  const [editGroupId, setEditGroupId] = useState<string | null>(null);

  const { user } = useProfile();
  const { conversations: rawConversations, userProfiles } = useConversations();
  const currentUser = useAuthStore((state) => state.user);
  const { groups, createGroupConversation } = useGroupConversations();

  const { userMap, addUsers, needsUpdate } = useUserMapStore();

  const userProfileIds = useMemo(
    () => Object.keys(userProfiles),
    [userProfiles],
  );

  const loadUserMap = useCallback(async () => {
    if (userProfileIds.length === 0) return;
    if (needsUpdate(userProfileIds)) {
      const newUserIds = userProfileIds.filter((id) => !(id in userMap));
      if (newUserIds.length > 0) {
        const newUsers = await buildUserMap(newUserIds, currentUser as any);
        addUsers(newUsers);
      }
    }
  }, [userProfileIds, currentUser, needsUpdate, userMap, addUsers]);

  useEffect(() => {
    loadUserMap();
  }, [loadUserMap]);

  const conversations = rawConversations.map((conv) => ({
    ...conv,
    type: conv.type || (conv.participants.length === 2 ? "direct" : "group"),
  }));

  const justOpenedRef = useRef(false);

  useEffect(() => {
    if (isDropdownOpen) {
      justOpenedRef.current = true;
      const timer = setTimeout(() => {
        justOpenedRef.current = false;
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isDropdownOpen]);

  useEffect(() => {
    const clickHandler = ({ target }: MouseEvent) => {
      if (!dropdown.current) return;
      if (!isDropdownOpen || dropdown.current.contains(target as Node)) return;
      if (justOpenedRef.current) return;
      const targetElement = target as HTMLElement;
      if (targetElement.closest("[data-chitchat-trigger]")) return;
      closeDropdown();
    };
    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  }, [isDropdownOpen, closeDropdown]);

  useEffect(() => {
    const keyHandler = ({ keyCode }: KeyboardEvent) => {
      if (!isDropdownOpen || keyCode !== 27) return;
      closeDropdown();
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  });

  const handleConversationSelect = async (conversationId: string) => {
    const conv = conversations.find((c) => c.id === conversationId);
    if (!conv) return;

    let chatUser: User | undefined;
    let chatGroup: Conversation | undefined;
    let title = "";

    if (conv.type === "direct") {
      const otherUserId = conv.participants.find((id) => id !== user?.id);
      if (otherUserId) {
        chatUser = userMap[otherUserId] as unknown as User;
        title = (chatUser as any)?.profile?.name || "Chat";
      }
    } else {
      chatGroup = groups.find((g) => g.id === conversationId);
      title = chatGroup?.title || "Group Chat";
    }

    openChatWindow({
      conversationId,
      conversationType: conv.type as "direct" | "group",
      title,
      user: chatUser,
      group: chatGroup,
    });
  };

  const handleGroupModalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!groupTitle.trim() || selectedUsers.length === 0) {
      toast.error("Please enter a group name and select at least one member.");
      return;
    }
    const participantIds = Array.from(new Set([...selectedUsers, user?.id!]));
    if (editGroupId) {
      try {
        await GroupService.updateGroup(editGroupId, {
          title: groupTitle,
          participants: participantIds,
          color,
        });
        setShowCreateGroupModal(false);
        setEditGroupId(null);
        toast.success("Group updated!");
      } catch {
        toast.error("Error updating group");
      }
    } else {
      const groupId = await createGroupConversation(
        groupTitle,
        participantIds,
        color,
      );
      if (groupId) {
        setShowCreateGroupModal(false);
        clearGroupModalState();
        handleConversationSelect(groupId);
        toast.success("Group created successfully!");
      } else {
        toast.error("Failed to create group. Please try again.");
      }
    }
  };

  const openEditGroupModal = (group: Conversation) => {
    if (group.createdBy !== user?.id) {
      toast.error("Only the creator of the group can edit it.");
      return;
    }
    setEditGroupId(group.id);
    setGroupTitle(group.title || "");
    setSelectedUsers(
      group.participants?.filter((id: string) => id !== user?.id) || [],
    );
    setColor(group.color || "#6366f1");
    setShowCreateGroupModal(true);
  };

  const clearGroupModalState = () => {
    setEditGroupId(null);
    setGroupTitle("");
    setSelectedUsers([]);
    setColor("#6366f1");
  };

  return (
    <>
      <Transition
        className={`origin-top-right z-20 absolute top-full min-w-80 md:max-w-96 max-w-[calc(100vw-1rem)] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700/60 rounded-lg shadow-lg overflow-hidden mt-1 left-[-100px] md:left-auto ${
          align === "right" ? "md:right-0" : "md:left-0"
        }`}
        show={isDropdownOpen}
        enter="transition ease-out duration-200 transform"
        enterStart="opacity-0 -translate-y-2"
        enterEnd="opacity-100 translate-y-0"
        leave="transition ease-out duration-200"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
        appear={undefined}
      >
        <div
          ref={dropdown}
          className="max-h-[70vh] overflow-y-auto"
          onFocus={() => {}}
          onBlur={() => {}}
        >
          <div className="px-4 py-3">
            <div className="flex items-center gap-2 mb-3">
              <div className="flex-1">
                <SearchForm placeholder="Search..." onChange={setSearchTerm} />
              </div>
              <button
                type="button"
                className="rounded-full h-9 w-9 bg-slate-100 hover:bg-blue-100 text-blue-600 flex items-center justify-center"
                onClick={() => {
                  clearGroupModalState();
                  setShowCreateGroupModal(true);
                }}
                title="Create group"
              >
                <PlusIcon size={20} className="h-6 w-6" />
              </button>
            </div>

            <DirectMessages
              setMsgSidebarOpen={() => {}}
              selectedConversation=""
              setSelectedConversation={(id) => {
                if (typeof id === "string") {
                  handleConversationSelect(id);
                }
              }}
              searchTerm={searchTerm}
            />

            <div className="mt-4">
              <div
                className={`text-xs font-semibold text-gray-400 ${theme.text.muted} mb-2`}
              >
                Groups
              </div>
              <ul>
                {(() => {
                  const filteredGroups = groups.filter((group) => {
                    if (!searchTerm || searchTerm.trim() === "") return true;
                    return group.title
                      ?.toLowerCase()
                      .includes(searchTerm.toLowerCase());
                  });

                  if (filteredGroups.length === 0) {
                    return (
                      <li className="py-2 text-center">
                        <span className={`text-sm ${theme.text.muted}`}>
                          No groups found
                        </span>
                      </li>
                    );
                  }

                  return filteredGroups.map((group) => {
                    const hasUnread = group.unreadCount > 0;
                    const isCreator = group.createdBy === user?.id;
                    return (
                      <li className="-mx-2" key={group.id}>
                        <div
                          className={`flex items-center justify-between w-full p-2 rounded-lg ${
                            hasUnread
                              ? "bg-amber-100 dark:bg-amber-900/50 hover:bg-amber-200 dark:hover:bg-amber-900/70"
                              : "hover:bg-slate-50 dark:hover:bg-slate-800"
                          }`}
                        >
                          <button
                            className="flex items-center truncate flex-1"
                            onClick={() => handleConversationSelect(group.id)}
                          >
                            <span
                              className="flex items-center justify-center w-8 h-8 rounded-full mr-2"
                              style={{ backgroundColor: "#111827" }}
                            >
                              <svg
                                width="20"
                                height="20"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke={group.color || "#6366f1"}
                                strokeWidth={2}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M7 17l2-10m4 10l2-10M5 9h14M5 15h14"
                                />
                              </svg>
                            </span>
                            <span
                              className={`text-sm font-medium ${theme.text.primary} truncate`}
                            >
                              {group.title}
                            </span>
                          </button>
                          <div className="flex items-center gap-1 ml-2">
                            {isCreator && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEditGroupModal(group);
                                }}
                                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                title="Edit group"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth={2}
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                  />
                                </svg>
                              </button>
                            )}
                            {hasUnread && (
                              <div className="text-xs inline-flex font-medium bg-amber-500 text-white rounded-full text-center leading-5 px-2">
                                {group.unreadCount}
                              </div>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  });
                })()}
              </ul>
            </div>
          </div>
        </div>
      </Transition>

      <CreateGroupModal
        open={showCreateGroupModal}
        onClose={() => {
          setShowCreateGroupModal(false);
          clearGroupModalState();
        }}
        groupTitle={groupTitle}
        setGroupTitle={setGroupTitle}
        selectedUsers={selectedUsers}
        setSelectedUsers={setSelectedUsers}
        userProfiles={userProfiles}
        onSubmit={handleGroupModalSubmit}
        color={color}
        setColor={setColor}
        editMode={!!editGroupId}
        canEdit={(() => {
          if (!editGroupId) return true;
          const group = groups.find((g) => g.id === editGroupId);
          return group?.createdBy === user?.id;
        })()}
      />
    </>
  );
};

export const ChitChatDropdown: React.FC<ChitChatDropdownProps> = (props) => {
  return (
    <ConversationsProvider>
      <ChitChatDropdownContent {...props} />
    </ConversationsProvider>
  );
};

export default ChitChatDropdown;
